/**
 * feAI – Frontend
 * src/hooks/useAuth.ts
 *
 * Central auth state machine hook.
 * Manages the three possible states:
 *   idle → loading → authenticated  (single account)
 *   idle → loading → multi_account → authenticated  (multi-account flow)
 *
 * Persists the access token in localStorage so the session survives a refresh.
 */

import { useState, useCallback, useRef } from 'react';
import { apiLogin, apiSelectProfile, apiSsoLogin } from '../lib/authApi';
import type { AuthStep, AuthContextValue, ProfileCard } from '../types/auth';

const TOKEN_KEY = 'feai_access_token';

function getInitialStep(): AuthStep {
  const stored = localStorage.getItem(TOKEN_KEY);
  return stored
    ? { type: 'authenticated', accessToken: stored }
    : { type: 'idle' };
}

export function useAuth(): AuthContextValue {
  const [step, setStep] = useState<AuthStep>(getInitialStep);
  const [error, setError] = useState<string | null>(null);

  // ── Holds the temp token and profiles during multi-account flow ───────────
  const [pendingTempToken, setPendingTempToken] = useState<string | null>(null);
  const pendingProfilesRef = useRef<ProfileCard[]>([]);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = useCallback(async (identificativo: string, password: string) => {
    setError(null);
    setStep({ type: 'loading' });

    try {
      const result = await apiLogin({ identificativo, password });

      if (result.status === 'OK') {
        localStorage.setItem(TOKEN_KEY, result.access_token);
        setStep({ type: 'authenticated', accessToken: result.access_token });
        return;
      }

      if (result.status === 'MUST_RESET') {
        setStep({
          type:              'must_reset',
          channel:           result.channel,
          maskedDestination: result.maskedDestination,
        });
        return;
      }

      // MULTI_ACCOUNT
      setPendingTempToken(result.temp_token);
      pendingProfilesRef.current = result.profiles;
      setStep({
        type:      'multi_account',
        tempToken: result.temp_token,
        profiles:  result.profiles,
      });
    } catch (err) {
      setStep({ type: 'idle' });
      setError((err as Error).message ?? 'Errore durante il login');
    }
  }, []);

  // ── SSO login (from legacy server) ────────────────────────────────────────
  // Con retry automatico per errori TRANSITORI (rete, timeout, 5xx).
  // Gli errori definitivi (401 HMAC/expired, 404 utente) non vengono ritentati
  // perché ripetere la stessa richiesta non li risolverebbe.
  const ssoLogin = useCallback(async (
    ssoToken: string,
    ssoCodAgente: number,
    ssoUsername: string,
    ssoTs: number
  ) => {
    setError(null);
    setStep({ type: "loading" });

    // IMP: Rimuove SUBITO il vecchio token prima della chiamata API.
    // Questo previene due bug critici:
    //   1. Se la chiamata SSO fallisce, non rimane il vecchio token in localStorage
    //      che farebbe rientrare l'utente precedente al redirect su /login
    //   2. La chiamata API SSO non deve usare l'header Authorization del vecchio token
    localStorage.removeItem(TOKEN_KEY);

    const MAX_ATTEMPTS = 3;
    const BASE_DELAY_MS = 600;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const result = await apiSsoLogin({
          sso_token: ssoToken,
          sso_cod_agente: ssoCodAgente,
          sso_username: ssoUsername,
          sso_ts: ssoTs,
        });

        localStorage.setItem(TOKEN_KEY, result.access_token);
        setStep({ type: "authenticated", accessToken: result.access_token });
        return;
      } catch (err) {
        const status = (err as { status?: number }).status;

        // Errori definitivi: interrompi subito, non serve riprovare
        if (status && status !== 500 && status !== 502 && status !== 503 && status !== 504) {
          setStep({ type: "idle" });
          setError((err as Error).message ?? "Errore SSO");
          return;
        }

        // Errore transitorio (rete, timeout, 5xx): ritenta con backoff
        const isLast = attempt === MAX_ATTEMPTS;
        if (isLast) {
          setStep({ type: "idle" });
          setError((err as Error).message ?? "Errore SSO, riprova");
          return;
        }
        await new Promise((r) => setTimeout(r, BASE_DELAY_MS * attempt));
      }
    }
  }, []);

  // ── Select profile (multi-account) ───────────────────────────────────────
  const selectProfile = useCallback(
    async (userId: string) => {
      if (!pendingTempToken) {
        setError('Sessione scaduta, effettua nuovamente il login');
        setStep({ type: 'idle' });
        return;
      }

      setError(null);
      setStep({ type: 'loading' });

      try {
        const result = await apiSelectProfile({
          temp_token: pendingTempToken,
          user_id:    userId,
        });

        localStorage.setItem(TOKEN_KEY, result.access_token);
        setPendingTempToken(null);
        setStep({ type: 'authenticated', accessToken: result.access_token });
      } catch (err) {
        // Go back to profile-picker so the user can retry
        setStep({
          type:      'multi_account',
          tempToken: pendingTempToken,
          profiles:  pendingProfilesRef.current,
        });
        setError((err as Error).message ?? 'Errore durante la selezione del profilo');
      }
    },
    [pendingTempToken]
  );

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setPendingTempToken(null);
    setError(null);
    setStep({ type: 'idle' });
  }, []);

  return { step, login, ssoLogin, selectProfile, logout, error };
}
